import React, { FC, ReactNode, useEffect, useState } from 'react';
import { clone } from 'lodash';
import { useLocation } from 'react-router';

import { PostAction, PostState, UserGroup } from 'types/app';
import { useCurrentUser, useFollowed, useNotify } from 'hooks';
import { USER_FEED_ID } from 'constants/index';
import { isGroupOwnerOrModerator } from 'features/groups/helpers';
import { isAdminUser } from 'helpers';

import { HandleGroupToggle, IPostForm, IPostRenderProps } from '../types';
import { CreatorEditor, ManualEditor, ModeratorEditor } from '../components';

import CreatePostForm from './CreatePostForm';
import EditPostForm from './EditPostForm';

interface IProps {
  action: PostAction;
  children: (props: IPostRenderProps) => ReactNode;
  groupIds?: string[];
  selectedGroup?: UserGroup;
}

interface IState {
  postState: PostState;
  isChanging: boolean;
  isWorking: boolean;
}

const Post: FC<IProps> = ({ action, children, selectedGroup, groupIds }) => {
  const [state, setState] = useState<IState>({
    postState: PostState.View,
    isChanging: false,
    isWorking: false,
  });
  const location = useLocation();

  if (action === PostAction.Create) {
    useEffect(() => {
      const searchParams = new URLSearchParams(location.search);
      const shareUrl = searchParams.get('share-url');
      if (shareUrl) {
        setState({
          postState: PostState.Edit,
          isChanging: true,
          isWorking: state.isWorking,
        });
      }
    }, []);
  }

  const { postable } = useFollowed();
  const currentUser = useCurrentUser();
  const notify = useNotify();

  function getOrderedGroups() {
    const { list } = postable;

    if (!selectedGroup) {
      return list;
    }

    const clonedList = clone(list);
    const index = clonedList.findIndex(item => item.id === selectedGroup.id);
    const removed = clonedList.splice(index, 1);

    return removed.concat(clonedList);
  }

  function getGroupsWithoutTimeline() {
    const { list } = postable;
    return list.filter((group: UserGroup) => group.id !== USER_FEED_ID);
  }

  function toggleState(postState: PostState) {
    const { isChanging } = state;
    setState(prevState => ({ ...prevState, postState, isChanging: !isChanging, isWorking: false }));
  }

  function toggleWork() {
    const { isWorking } = state;
    setState(prevState => ({ ...prevState, isWorking: !isWorking }));
  }

  const handleGroupToggle: HandleGroupToggle = values => (group, arrayHelpers) => {
    const { group_ids } = values;
    const currentIndex = group_ids.findIndex(id => id === group.id);

    currentIndex >= 0 ? arrayHelpers.remove(currentIndex) : arrayHelpers.push(group.id);
  };

  const handleError: IPostForm['handleError'] = () => {
    toggleWork();
    notify.mutationError();
  };

  const handleCompleted: IPostForm['handleCompleted'] = (result, options) => {
    const { i18nKey, showNotification } = options;

    toggleWork();
    toggleState(PostState.View);

    if (showNotification) {
      notify.info({ i18n: { i18nKey } });
    }
  };

  const renderEditor: IPostForm['renderEditor'] = props => {
    const { values } = props;
    const { isWorking, postState } = state;

    const groupsExcludingTimeline = getGroupsWithoutTimeline();
    const orderedBySelectedGroups = getOrderedGroups();

    const isEditing = postState === PostState.Edit;
    const isCustomizing = postState === PostState.Customize;
    const adminCanEdit = isCustomizing && isAdminUser(currentUser);
    const moderatorCanEdit = isGroupOwnerOrModerator(
      currentUser,
      selectedGroup && selectedGroup.id,
    );
    const groups = isEditing ? orderedBySelectedGroups : groupsExcludingTimeline;

    if (isEditing || adminCanEdit) {
      return (
        <CreatorEditor
          groups={groups}
          action={action}
          working={isWorking}
          canModerate={moderatorCanEdit}
          onClose={() => toggleState(PostState.View)}
          onGroupToggle={handleGroupToggle(values)}
          selectedGroup={selectedGroup}
        />
      );
    }

    if (isCustomizing && moderatorCanEdit) {
      return (
        <ModeratorEditor
          groups={groups}
          working={isWorking}
          onClose={() => toggleState(PostState.View)}
          onGroupToggle={handleGroupToggle(values)}
          selectedGroup={selectedGroup}
        />
      );
    }

    return (
      <ManualEditor
        groups={groups}
        working={isWorking}
        onClose={() => {
          toggleState(PostState.View);
        }}
        onGroupToggle={handleGroupToggle(values)}
        selectedGroup={selectedGroup}
      />
    );
  };

  const ModeComponent = action === PostAction.Create ? CreatePostForm : EditPostForm;
  const renderProps: IPostForm = {
    handleError,
    handleCompleted,
    renderEditor,
  };

  const stateProps: IPostRenderProps = {
    selectedGroup,
    groupIds,
    toggleState,
    toggleWork,
    isChanging: state.isChanging,
  };

  return (
    <ModeComponent {...renderProps} {...stateProps}>
      {children(stateProps)}
    </ModeComponent>
  );
};

export default Post;
